import React, { useEffect, useState } from 'react'
import { Form, Button, Divider, Checkbox, Input, InputNumber, Slider, Typography } from 'antd'
import { DownOutlined, UpOutlined, DeleteOutlined } from '@ant-design/icons'
import { Layer, LayerAttributeNumber } from '../../../services/models'
import {
  appliedFilterCount,
  asFormNumber,
  attributeLabel,
  DictionaryUi,
  formatBoundary,
  GroupFilters,
  isNumberFilterSet,
  isOutOfLayerRange,
  LayerGroup,
  NumberRange,
  NUM_MAX,
  NUM_MIN,
  RawGroupForm,
  rangeLabel,
  sliderStep,
  toFilters,
  toInitialForm
} from './types'

const { Text } = Typography

type NumberFormApi = ReturnType<typeof Form.useForm>[0]

// Ползунок и два поля ввода — две проекции одних и тех же границ, поэтому
// состояние диапазона держим здесь и пишем в форму вручную: antd не умеет
// связать Slider range с двумя разными Form.Item без дублирования полей.
const NumberAttribute: React.FC<{ attr: LayerAttributeNumber, form: NumberFormApi }> = ({ attr, form }) => {
  const nameMin = `n:${attr.id}${NUM_MIN}`
  const nameMax = `n:${attr.id}${NUM_MAX}`

  // Атрибут может висеть на слое, но не иметь ни одного значения — тогда
  // границ нет и шкалу построить не на чем.
  const hasData = attr.min !== null && attr.max !== null
  const min = attr.min ?? 0
  const max = attr.max ?? 0

  const [low, setLow] = useState<number | null>(null)
  const [high, setHigh] = useState<number | null>(null)

  // Инициализация из применённых фильтров группы. Форма здесь одна на группу,
  // поэтому читаем её через getFieldsValue, а не через initialValues.
  useEffect(() => {
    const values = form.getFieldsValue() as RawGroupForm
    setLow(asFormNumber(values[nameMin]))
    setHigh(asFormNumber(values[nameMax]))
  }, [form, nameMin, nameMax])

  // Единственная точка записи границ: сортирует их, поэтому введённые вручную
  // min > max тоже невозможны — значения просто меняются местами.
  const push = (nextLow: number | null, nextHigh: number | null) => {
    const ordered = nextLow !== null && nextHigh !== null && nextLow > nextHigh
      ? [nextHigh, nextLow]
      : [nextLow, nextHigh]
    setLow(ordered[0])
    setHigh(ordered[1])
    form.setFieldsValue({
      [nameMin]: ordered[0] ?? undefined,
      [nameMax]: ordered[1] ?? undefined
    })
  }

  const lowOut = hasData && isOutOfLayerRange(low, min, max)
  const highOut = hasData && isOutOfLayerRange(high, min, max)

  if (!hasData) {
    return (
      <div style={{ marginBottom: 10 }}>
        <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>{attributeLabel(attr)}</Text>
        <Slider range disabled min={0} max={1} />
        <Text type="secondary" style={{ fontSize: '12px' }}>нет данных в слое</Text>
      </div>
    )
  }

  return (
    <div style={{ marginBottom: 10 }}>
      <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
        {attributeLabel(attr)} · {formatBoundary(min)} – {formatBoundary(max)}
      </Text>
      {/* antd не пробрасывает allowCross из rc-slider, поэтому запрет на пересечение
          ручек держим сами — в push границы всегда сортируются. */}
      <Slider
        range
        min={min}
        max={max}
        step={sliderStep(min, max)}
        value={[low ?? min, high ?? max]}
        onChange={values => push(values[0], values[1])}
      />
      <div style={{ display: 'flex', gap: 6 }}>
        <Form.Item
          name={nameMin}
          style={{ flex: 1, marginBottom: 0 }}
          validateStatus={lowOut ? 'error' : undefined}
          help={lowOut ? 'вне диапазона слоя' : undefined}
        >
          <InputNumber
            size="small"
            style={{ width: '100%' }}
            placeholder="от"
            controls={false}
            step={sliderStep(min, max)}
            onChange={value => push(asFormNumber(value), high)}
          />
        </Form.Item>
        <Form.Item
          name={nameMax}
          style={{ flex: 1, marginBottom: 0 }}
          validateStatus={highOut ? 'error' : undefined}
          help={highOut ? 'вне диапазона слоя' : undefined}
        >
          <InputNumber
            size="small"
            style={{ width: '100%' }}
            placeholder="до"
            controls={false}
            step={sliderStep(min, max)}
            onChange={value => push(low, asFormNumber(value))}
          />
        </Form.Item>
      </div>
    </div>
  )
}

type Props = {
  group: LayerGroup,
  layer: Layer,
  dictUi: DictionaryUi,
  onApply: (groupKey: string, filters: GroupFilters) => void,
  onRemove: (groupKey: string) => void
}

const GroupCard: React.FC<Props> = ({ group, layer, dictUi, onApply, onRemove }) => {
  // Своя форма на каждую группу: useForm нельзя вызывать в цикле, а черновики
  // фильтров разных групп должны жить независимо друг от друга.
  const [form] = Form.useForm()

  // initialValues срабатывает при монтировании, а карточки размонтируются при
  // сворачивании панели — поэтому при повторном открытии форма восстанавливает
  // именно применённые фильтры.
  // Какие числовые границы сейчас применены — показываем текстом, потому что
  // на линейной шкале с хвостом (1–8865) тонкую подпись с ручки не прочитать.
  const appliedRanges = (layer.attributeNumbers ?? [])
    .filter(an => isNumberFilterSet(group.filters, an.id))
    .map(an => `${attributeLabel(an)}: ${rangeLabel(group.filters, an.id)}`)

  const status = group.loading
    ? 'Загрузка...'
    : !group.applied
        ? 'Нажмите «Применить», чтобы показать точки'
        : group.items.length === 0
          ? 'Ничего не найдено'
          : `Точек: ${group.items.length}`

  // Границы за пределами [min, max] слоя в запрос не идут: такой поиск вернул бы
  // пустую выдачу, и выглядело бы как сломанный фильтр. Поля при этом остаются
  // подсвечены, так что причина видна сразу.
  const handleApply = () => {
    const filters = toFilters(form.getFieldsValue() as RawGroupForm)
    const attrs = layer.attributeNumbers ?? []
    const cleaned: GroupFilters = { ...filters }
    attrs.forEach(an => {
      const key = `n:${an.id}`
      const range = cleaned[key] as NumberRange | undefined
      if (!range || an.min === null || an.max === null) return
      const low = isOutOfLayerRange(range[0], an.min, an.max) ? null : range[0]
      const high = isOutOfLayerRange(range[1], an.min, an.max) ? null : range[1]
      if (low === null && high === null) {
        delete cleaned[key]
      } else {
        cleaned[key] = [low, high]
      }
    })
    onApply(group.key, cleaned)
  }

  return (
    <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 10, marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
        <span style={{ width: 12, height: 12, borderRadius: '50%', background: group.color, flex: '0 0 auto' }} />
        <Text strong ellipsis style={{ flex: 1 }}>{layer.name}</Text>
        <Button
          size="small"
          type="text"
          danger
          icon={<DeleteOutlined/>}
          title={'Убрать слой с карты'}
          onClick={() => onRemove(group.key)}
        />
      </div>

      <Form form={form} layout="vertical" initialValues={toInitialForm(group.filters)}>
        {(layer.attributeNumbers ?? []).length > 0 && (
          <div>
            <Divider plain orientation="center">Числовые атрибуты</Divider>
            {(layer.attributeNumbers ?? []).map(an => (
              <NumberAttribute key={`n:${an.id}`} attr={an} form={form} />
            ))}
          </div>
        )}
        {(layer.attributeDictionaries ?? []).map(ad => (
          <div key={`d:${ad.id}`}>
            <Divider plain orientation="center">{ad.name}</Divider>
            {dictUi.showMore[ad.id] && (
              <Input
                allowClear
                size="small"
                placeholder="Поиск..."
                value={dictUi.search[ad.id]}
                onChange={(e) => dictUi.setSearch(ad.id, e.target.value)}
              />
            )}
            <Form.Item name={`d:${ad.id}`} style={{ maxHeight: '150px', overflow: 'scroll', margin: 0 }}>
              <Checkbox.Group>
                {dictUi.optionsFor(ad).map(v => (
                  <Checkbox key={v.id.toString()} value={v.id} style={{ width: '100%' }}>{v.name}</Checkbox>
                ))}
              </Checkbox.Group>
            </Form.Item>
            {ad.values?.length === 5 &&
              <div onClick={() => dictUi.toggleShowMore(ad.id)} style={{
                fontSize: '12px',
                color: 'gray',
                cursor: 'pointer',
                margin: '3px',
                textAlign: 'center'
              }}>
                {dictUi.showMore[ad.id]
                  ? <>Свернуть <UpOutlined/></>
                  : <>Показать всё <DownOutlined/></>
                }
              </div>
            }
          </div>
        ))}
        <Button type="primary" htmlType="button" block loading={group.loading} onClick={handleApply}>
          Применить
        </Button>
      </Form>

      {appliedRanges.length > 0 && (
        <div style={{ fontSize: '12px', color: '#888', marginTop: 4 }}>
          {appliedRanges.join(', ')}
        </div>
      )}
      <div style={{ fontSize: '12px', color: '#888', marginTop: 6 }}>
        {status}
        {group.applied && !group.loading && appliedFilterCount(group.filters) > 0 &&
          ` · фильтров: ${appliedFilterCount(group.filters)}`
        }
      </div>
    </div>
  )
}

export default GroupCard
